import * as z from 'zod';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { dateOnlyOf, toDateOnlyValue } from '@/lib/utils/custom-date/custom-date';
import {
  EmergencyContact,
  Employee,
  EmployeeAddress,
  EmployeeInsertInput,
  EmployeeReference,
} from '../types/employee.types';

/** Number of reference groups the legacy step always shows. */
export const CONTACT_REFERENCE_COUNT = 2;

/** Legacy limit: the hobbies chip input stops accepting entries after three. */
export const MAX_HOBBIES = 3;

/** Optional international phone number; when filled it must be a valid number (the legacy phone component's check). */
const optionalPhoneSchema = z
  .string()
  .optional()
  .refine((value) => !value || isValidPhoneNumber(value), { message: 'PHONE_NUMBER_INVALID' });

/** True when the value is a syntactically valid email address (empty is not). */
const isValidEmailAddress = (value?: string): boolean =>
  z
    .string()
    .email()
    .safeParse(value ?? '').success;

/** Legacy `Validators.email` on an otherwise optional field: empty passes, anything else must be an email. */
const optionalEmailSchema = z
  .string()
  .trim()
  .optional()
  .refine((value) => !value || isValidEmailAddress(value), { message: 'EMAIL_INVALID' });

const requiredEmailSchema = z
  .string()
  .trim()
  .min(1, { message: 'ERR_MSG_REQUIRED' })
  .email({ message: 'EMAIL_INVALID' });

const contactReferenceSchema = z.object({
  Name: z.string().optional(),
  Designation: z.string().optional(),
  Company: z.string().optional(),
  Phone: optionalPhoneSchema,
  Email: optionalEmailSchema,
});

export type ContactReferenceValues = z.infer<typeof contactReferenceSchema>;

export const contactInfoSchema = z.object({
  // Business
  Email: requiredEmailSchema,
  PhoneNumber: optionalPhoneSchema,

  // Private
  IsPrivateInfoVisible: z.boolean().default(false),
  PrivateEmail: requiredEmailSchema,
  PrivatePhoneNumber: optionalPhoneSchema,

  // Emergency contact
  EmergencyContactName: z.string().optional(),
  EmergencyContactPhoneNumber: optionalPhoneSchema,
  EmergencyContactRelationship: z.string().optional(),
  /** Not on the form; kept so a save does not drop an email stored with the contact. */
  EmergencyContactEmail: z.string().optional(),

  // Present address
  Country: z.string().optional(),
  Canton: z.string().optional(),
  /** LocationId of the selected canton, kept alongside its name like the legacy payload. */
  CantonId: z.string().optional(),
  City: z.string().optional(),
  ZipCode: z.string().optional(),
  Address: z.string().optional(),

  // Permanent address
  IsSameAsPresentAddress: z.boolean().default(false),
  PermanentCountry: z.string().optional(),
  PermanentCanton: z.string().optional(),
  PermanentCantonId: z.string().optional(),
  PermanentCity: z.string().optional(),
  PermanentZipCode: z.string().optional(),
  PermanentAddress: z.string().optional(),

  // Social profiles
  LinkedInProfile: z.string().optional(),
  GithubProfile: z.string().optional(),
  FaceBookProfile: z.string().optional(),
  WhatsAppProfile: z.string().optional(),
  InstagramProfile: z.string().optional(),
  ProfessionalImageUrl: z.string().optional(),

  // Reference details (Bangladesh only)
  References: z.array(contactReferenceSchema),

  // Personal details
  FathersName: z.string(),
  MothersName: z.string(),
  SpouseName: z.string(),
  BloodGroup: z.string(),
  PassportNo: z.string(),
  PassportExpiryDate: z.string(),
  Nid: z.string(),
  Etin: z.string(),
  HobbiesInterests: z.array(z.string()).max(MAX_HOBBIES),

  // Academic information and previous employment
  LatestAcademicTitle: z.string(),
  GraduationInstitute: z.string(),
  PostGraduationInstitute: z.string(),
  PreviousExperience: z.string(),
  PreviousOrganization: z.string(),
});

export type ContactInfoValues = z.infer<typeof contactInfoSchema>;

const buildEmptyReferences = (): ContactReferenceValues[] =>
  Array.from({ length: CONTACT_REFERENCE_COUNT }, () => ({
    Name: '',
    Designation: '',
    Company: '',
    Phone: '',
    Email: '',
  }));

export const contactInfoDefaults: ContactInfoValues = {
  Email: '',
  PhoneNumber: '',
  IsPrivateInfoVisible: false,
  PrivateEmail: '',
  PrivatePhoneNumber: '',
  EmergencyContactName: '',
  EmergencyContactPhoneNumber: '',
  EmergencyContactRelationship: '',
  EmergencyContactEmail: '',
  Country: '',
  Canton: '',
  CantonId: '',
  City: '',
  ZipCode: '',
  Address: '',
  IsSameAsPresentAddress: false,
  PermanentCountry: '',
  PermanentCanton: '',
  PermanentCantonId: '',
  PermanentCity: '',
  PermanentZipCode: '',
  PermanentAddress: '',
  LinkedInProfile: '',
  GithubProfile: '',
  FaceBookProfile: '',
  WhatsAppProfile: '',
  InstagramProfile: '',
  ProfessionalImageUrl: '',
  References: buildEmptyReferences(),
  FathersName: '',
  MothersName: '',
  SpouseName: '',
  BloodGroup: '',
  PassportNo: '',
  PassportExpiryDate: '',
  Nid: '',
  Etin: '',
  HobbiesInterests: [],
  LatestAcademicTitle: '',
  GraduationInstitute: '',
  PostGraduationInstitute: '',
  PreviousExperience: '',
  PreviousOrganization: '',
};

const toText = (value: unknown): string => (typeof value === 'string' ? value : '');

/** The stored references -> exactly CONTACT_REFERENCE_COUNT reference groups (legacy read References[0] and [1]). */
const toReferenceValues = (stored?: EmployeeReference[] | null): ContactReferenceValues[] =>
  buildEmptyReferences().map((emptyReference, index) => {
    const source = stored?.[index] ?? {};
    return {
      ...emptyReference,
      Name: toText(source.Name),
      Designation: toText(source.Designation),
      Company: toText(source.Company),
      Phone: toText(source.Phone),
      Email: toText(source.Email),
    };
  });

const isReferenceFilled = (reference: ContactReferenceValues): boolean =>
  [reference.Name, reference.Designation, reference.Company, reference.Phone, reference.Email].some(
    (field) => Boolean(field?.trim())
  );

/** The emergency contact as the form's flat `EmergencyContact*` fields. */
export const emergencyContactValues = (contact?: EmergencyContact | null) => ({
  EmergencyContactName: toText(contact?.Name),
  EmergencyContactPhoneNumber: toText(contact?.PhoneNumber),
  EmergencyContactRelationship: toText(contact?.Relationship),
  EmergencyContactEmail: toText(contact?.Email),
});

/** The present address as the form's flat address fields. */
export const presentAddressValues = (address?: EmployeeAddress | null) => ({
  Country: toText(address?.Country),
  Canton: toText(address?.Canton),
  CantonId: toText(address?.CantonId),
  City: toText(address?.City),
  ZipCode: toText(address?.ZipCode),
  Address: toText(address?.Address),
});

const permanentAddressValues = (address?: EmployeeAddress | null) => ({
  PermanentCountry: toText(address?.Country),
  PermanentCanton: toText(address?.Canton),
  PermanentCantonId: toText(address?.CantonId),
  PermanentCity: toText(address?.City),
  PermanentZipCode: toText(address?.ZipCode),
  PermanentAddress: toText(address?.Address),
});

export const contactInfoFromEmployee = (employee: Employee): Partial<ContactInfoValues> => ({
  Email: employee.Email ?? '',
  PhoneNumber: employee.PhoneNumber ?? '',
  IsPrivateInfoVisible: employee.IsPrivateInfoVisible ?? false,
  PrivateEmail: employee.PrivateEmail ?? '',
  PrivatePhoneNumber: employee.PrivatePhoneNumber ?? '',
  ...emergencyContactValues(employee.EmergencyContact),
  ...presentAddressValues(employee.PresentAddress),
  IsSameAsPresentAddress: employee.IsSameAsPresentAddress ?? false,
  ...permanentAddressValues(employee.PermanentAddress),
  LinkedInProfile: employee.LinkedInProfile ?? '',
  GithubProfile: employee.GithubProfile ?? '',
  FaceBookProfile: employee.FaceBookProfile ?? '',
  WhatsAppProfile: employee.WhatsAppProfile ?? '',
  InstagramProfile: employee.InstagramProfile ?? '',
  ProfessionalImageUrl: employee.ProfessionalImageUrl ?? '',
  References: toReferenceValues(employee.References),
  FathersName: employee.FathersName ?? '',
  MothersName: employee.MothersName ?? '',
  SpouseName: employee.SpouseName ?? '',
  BloodGroup: employee.BloodGroup ?? '',
  PassportNo: employee.PassportNo ?? '',
  PassportExpiryDate: dateOnlyOf(employee.PassportExpiryDate),
  Nid: employee.Nid ?? '',
  Etin: employee.Etin ?? '',
  HobbiesInterests: employee.HobbiesInterests ?? [],
  LatestAcademicTitle: employee.LatestAcademicTitle ?? '',
  GraduationInstitute: employee.GraduationInstitute ?? '',
  PostGraduationInstitute: employee.PostGraduationInstitute ?? '',
  PreviousExperience: employee.PreviousExperience ?? '',
  PreviousOrganization: employee.PreviousOrganization ?? '',
});

/**
 * Saved as the business email / phone on the `UserProfileDetail` and the rest on the `UserContactDetail`.
 * An emptied passport expiry date is sent as `null`, since an omitted field is left as stored.
 */
export type ContactInfoPayload = Omit<Partial<EmployeeInsertInput>, 'PassportExpiryDate'> & {
  PassportExpiryDate?: string | null;
};

export const contactInfoToPayload = (values: ContactInfoValues): ContactInfoPayload => {
  const presentAddress: EmployeeAddress = {
    Country: values.Country ?? '',
    Canton: values.Canton ?? '',
    CantonId: values.CantonId ?? '',
    City: values.City ?? '',
    ZipCode: values.ZipCode ?? '',
    Address: values.Address ?? '',
  };
  const permanentAddress: EmployeeAddress = values.IsSameAsPresentAddress
    ? presentAddress
    : {
        Country: values.PermanentCountry ?? '',
        Canton: values.PermanentCanton ?? '',
        CantonId: values.PermanentCantonId ?? '',
        City: values.PermanentCity ?? '',
        ZipCode: values.PermanentZipCode ?? '',
        Address: values.PermanentAddress ?? '',
      };
  return {
    Email: values.Email.trim(),
    PhoneNumber: values.PhoneNumber ?? '',
    IsPrivateInfoVisible: values.IsPrivateInfoVisible,
    PrivateEmail: values.PrivateEmail.trim(),
    PrivatePhoneNumber: values.PrivatePhoneNumber ?? '',

    EmergencyContact: {
      Name: values.EmergencyContactName ?? '',
      Email: values.EmergencyContactEmail ?? '',
      PhoneNumber: values.EmergencyContactPhoneNumber ?? '',
      Relationship: values.EmergencyContactRelationship ?? '',
    },

    PresentAddress: presentAddress,
    IsSameAsPresentAddress: values.IsSameAsPresentAddress,
    PermanentAddress: permanentAddress,

    LinkedInProfile: values.LinkedInProfile ?? '',
    GithubProfile: values.GithubProfile ?? '',
    FaceBookProfile: values.FaceBookProfile ?? '',
    WhatsAppProfile: values.WhatsAppProfile ?? '',
    InstagramProfile: values.InstagramProfile ?? '',
    ProfessionalImageUrl: values.ProfessionalImageUrl ?? '',

    References: values.References.filter(isReferenceFilled).map((reference) => ({
      Name: reference.Name ?? '',
      Designation: reference.Designation ?? '',
      Company: reference.Company ?? '',
      Phone: reference.Phone ?? '',
      Email: (reference.Email ?? '').trim(),
    })),

    FathersName: values.FathersName,
    MothersName: values.MothersName,
    SpouseName: values.SpouseName,
    BloodGroup: values.BloodGroup,
    PassportNo: values.PassportNo,
    PassportExpiryDate: toDateOnlyValue(values.PassportExpiryDate) ?? null,
    Nid: values.Nid,
    Etin: values.Etin,
    HobbiesInterests: values.HobbiesInterests,

    LatestAcademicTitle: values.LatestAcademicTitle,
    GraduationInstitute: values.GraduationInstitute,
    PostGraduationInstitute: values.PostGraduationInstitute,
    PreviousExperience: values.PreviousExperience,
    PreviousOrganization: values.PreviousOrganization,
  };
};
